import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Users, FileText, CheckCircle, Clock, ArrowRight, XCircle, AlertCircle, Building2 } from "lucide-react";

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    total_artists: 0,
    total_brands: 0,
    total_submissions: 0,
    pending_agency: 0,
    pending_brand: 0,
    approved_count: 0,
    rejected_count: 0,
    draft_count: 0
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, submissionsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/admin/submissions`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setStats(statsRes.data);
        setRecentSubmissions(submissionsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const statCards = [
    { label: "Artists", value: stats.total_artists, icon: Users, color: "bg-[#00B4E5]" },
    { label: "Brand Partners", value: stats.total_brands, icon: Building2, color: "bg-[#F58220]" },
    { label: "Pending Agency", value: stats.pending_agency, icon: AlertCircle, color: "bg-orange-500" },
    { label: "Pending Brand", value: stats.pending_brand, icon: Clock, color: "bg-purple-500" },
    { label: "Approved", value: stats.approved_count, icon: CheckCircle, color: "bg-green-500" },
    { label: "Rejected", value: stats.rejected_count, icon: XCircle, color: "bg-[#ED5A34]" },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-500/20 text-gray-500",
      pending_agency_approval: "bg-orange-500/20 text-orange-500",
      pending_brand_approval: "bg-purple-500/20 text-purple-500",
      approved: "bg-green-500/20 text-green-500",
      rejected: "bg-[#ED5A34]/20 text-[#ED5A34]"
    };
    const labels = {
      draft: "Draft",
      pending_agency_approval: "Pending Agency",
      pending_brand_approval: "Pending Brand",
      approved: "Approved",
      rejected: "Rejected"
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-wider ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-4xl font-heading text-gray-900 mb-2">AGENCY DASHBOARD</h1>
          <p className="text-gray-600">Overview of campaign submissions and approvals</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {statCards.map((stat, index) => (
            <div 
              key={stat.label} 
              className="bg-white border border-gray-200 p-4 hover:border-[#00B4E5] transition-colors duration-300"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex flex-col items-center text-center">
                <div className={`${stat.color} p-2 text-white mb-2`}>
                  <stat.icon size={20} />
                </div>
                <p className="text-2xl font-heading text-gray-900">{loading ? "-" : stat.value}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Submissions */}
        <div className="bg-white border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-heading text-xl text-gray-900">RECENT SUBMISSIONS</h2>
            <Link 
              to="/admin/submissions" 
              className="text-[#00B4E5] hover:underline flex items-center gap-1 text-sm font-medium"
            >
              View All <ArrowRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : recentSubmissions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No submissions yet</div>
          ) : (
            <table className="kf-table">
              <thead>
                <tr className="bg-gray-50">
                  <th>Artist</th>
                  <th>Venue</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="font-medium text-gray-900">{submission.artist_name}</td>
                    <td className="text-gray-600">{submission.venue}</td>
                    <td className="text-gray-600">{submission.date}</td>
                    <td>{getStatusBadge(submission.status)}</td>
                    <td>
                      <Link 
                        to={`/admin/submissions/${submission.id}`}
                        className="text-[#00B4E5] hover:underline text-sm font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
